import { AsYouType, CountryCode, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js/max';
import examples from 'libphonenumber-js/mobile/examples';
import { E164_MAX_DIGITS } from './phone-format';

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'BR';

/** DDD (2) + celular (9 dígitos). */
const BR_NATIONAL_MAX_DIGITS = 11;

export interface PhoneCountry {
  iso2: CountryCode;
  name: string;
  dialCode: string;
  /** Quantidade máxima de dígitos do número nacional (sem DDI) para este país. */
  digitCap: number;
}

function countryName(iso2: string): string {
  try {
    const displayNames = new (Intl as any).DisplayNames(['pt'], { type: 'region' });
    return displayNames.of(iso2) || iso2;
  } catch (e) {
    return iso2;
  }
}

/**
 * Formata um número nacional (parcial ou completo) para exibição no campo:
 * tenta o AsYouType e, se ele não aplicar nenhuma máscara mas o número já for
 * parseável, recorre ao formatNational() oficial (cobre países cujo template só
 * existe com o prefixo de tronco, ex.: "0", que não faz parte do E.164).
 */
export function formatNationalMask(iso2: CountryCode, nationalValue: string): string {
  const formatted = new AsYouType(iso2).input(nationalValue);
  if (formatted !== nationalValue) {
    return formatted;
  }
  const parsed = parsePhoneNumberFromString(nationalValue, iso2);
  return parsed ? parsed.formatNational() : formatted;
}

// Brasil sempre em primeiro, os demais em ordem alfabética (pt-BR)
export const PHONE_COUNTRIES: PhoneCountry[] = getCountries()
  .map(iso2 => {
    const dialCode = getCountryCallingCode(iso2);
    return {
      iso2,
      name: countryName(iso2),
      dialCode,
      digitCap: iso2 === DEFAULT_PHONE_COUNTRY ? BR_NATIONAL_MAX_DIGITS : Math.max(E164_MAX_DIGITS - dialCode.length, 1)
    };
  })
  .sort((a, b) => {
    if (a.iso2 === DEFAULT_PHONE_COUNTRY) return -1;
    if (b.iso2 === DEFAULT_PHONE_COUNTRY) return 1;
    return a.name.localeCompare(b.name, 'pt-BR');
  });

const PHONE_COUNTRY_BY_ISO2 = new Map<string, PhoneCountry>(PHONE_COUNTRIES.map(country => [country.iso2, country]));

export function getPhoneCountry(iso2: CountryCode): PhoneCountry {
  return PHONE_COUNTRY_BY_ISO2.get(iso2) || PHONE_COUNTRY_BY_ISO2.get(DEFAULT_PHONE_COUNTRY)!;
}

const fieldMaxLengthCache = new Map<string, number>();

/**
 * Tamanho máximo do valor exibido (já mascarado) no campo, para o país.
 * Calculado sob demanda e memoizado: simula o pior caso de formatação com dígitos
 * repetidos e com o número de exemplo oficial do país.
 */
export function getFieldMaxLength(iso2: CountryCode): number {
  const cached = fieldMaxLengthCache.get(iso2);
  if (cached !== undefined) {
    return cached;
  }

  const digitCap = getPhoneCountry(iso2).digitCap;
  let max = digitCap;

  for (let digits = 1; digits <= digitCap; digits++) {
    max = Math.max(max, formatNationalMask(iso2, '9'.repeat(digits)).length);
  }

  // Alguns templates (ex.: prefixo de tronco) só aparecem para sequências plausíveis.
  const example: string | undefined = (examples as { [iso2: string]: string })[iso2];
  if (example) {
    const parsed = parsePhoneNumberFromString(example, iso2);
    const exampleNational = parsed ? parsed.nationalNumber.toString() : example;
    for (let digits = 1; digits <= exampleNational.length; digits++) {
      max = Math.max(max, formatNationalMask(iso2, exampleNational.slice(0, digits)).length);
    }
  }

  fieldMaxLengthCache.set(iso2, max);
  return max;
}
